import * as Sentry from "@sentry/react";

type SentrySpanContext = {
  readonly spanId: string;
  readonly traceFlags: number;
  readonly traceId: string;
};

const TRACE_ID_PATTERN = /^[0-9a-f]{32}$/iu;
const SPAN_ID_PATTERN = /^[0-9a-f]{16}$/iu;
const ALL_ZERO_TRACE_ID = "0".repeat(32);
const ALL_ZERO_SPAN_ID = "0".repeat(16);

/**
 * Mirrors Sentry's active browser span into standards-compatible W3C context.
 * This is auxiliary propagation only: during the current Cloudflare tracing
 * beta, native OTLP trace IDs do not join non-Cloudflare services. Sentry's
 * fetch integration therefore remains the application-trace authority through
 * its `sentry-trace` and `baggage` headers.
 */
export function formatSentryTraceparent(
  context: SentrySpanContext | null | undefined,
): string | null {
  if (
    !context ||
    !TRACE_ID_PATTERN.test(context.traceId) ||
    !SPAN_ID_PATTERN.test(context.spanId)
  ) {
    return null;
  }

  const traceId = context.traceId.toLowerCase();
  const spanId = context.spanId.toLowerCase();
  if (traceId === ALL_ZERO_TRACE_ID || spanId === ALL_ZERO_SPAN_ID) {
    return null;
  }

  const flags = (context.traceFlags & 1) === 1 ? "01" : "00";
  return `00-${traceId}-${spanId}-${flags}`;
}

export function getActiveSentryTraceparent(): string | null {
  try {
    return formatSentryTraceparent(Sentry.getActiveSpan()?.spanContext());
  } catch {
    return null;
  }
}
